import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = "https://effendialogue-2.onrender.com";
const API = `${BACKEND_URL}/api`;

const CategoryPage = () => {
  const { category } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryInfo, setCategoryInfo] = useState(null);

  useEffect(() => {
    fetchCategoryPosts();
    fetchCategoryInfo();
  }, [category]);

  useEffect(() => {
  document.title = "Effendialogue | Articles";
}, []);

  const fetchCategoryPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts?category=${category}`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching category posts:", error);
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryInfo = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const categoryData = response.data.find(cat => cat.id === category);
      setCategoryInfo(categoryData);
    } catch (error) {
      console.error("Error fetching category info:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen">
        <div className="content-wrapper py-20">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-700 rounded mb-8 w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-700 rounded-lg h-64"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      <div className="content-wrapper hero-section">
        {/* Category Header */}
        <div className="text-center mb-16 fade-in">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h1 className="text-5xl font-bold gradient-text capitalize">
              {categoryInfo?.name || category}
            </h1>
            <Badge className="category-badge">
              {posts.length} {posts.length === 1 ? 'Article' : 'Articles'}
            </Badge>
          </div>
          {categoryInfo && (
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              {categoryInfo.description}
            </p>
          )}
          
          <Link to="/">
            <Button variant="ghost" className="text-blue-400 hover:text-yellow-400 mb-8">
              ← Back to All Posts
            </Button>
          </Link>
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="text-center py-20">
            <h2 className="text-2xl text-gray-400 mb-4">No posts found in this category</h2>
            <p className="text-gray-500 mb-8">
              Check back soon for new articles in {categoryInfo?.name || category}
            </p>
            <Link to="/">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8">
                Browse All Articles
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <Card
                key={post.id}
                data-testid={`post-card-${post.id}`}
                className={`post-card slide-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {post.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform hover:scale-105"
                    />
                  </div>
                )}
                <CardHeader className="pb-3">
                  <div className="post-meta flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(post.created_at)}</span>
                    <User className="h-4 w-4 ml-2" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="category-badge text-xs">
                      {post.category}
                    </Badge>
                  </div>
                  <h3 className="post-title text-xl font-semibold line-clamp-2 transition-colors">
                    {post.title}
                  </h3>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="post-excerpt line-clamp-3 mb-4">
                    {post.excerpt || post.content.substring(0, 150) + "..."}
                  </p>
                  <Link 
                    to={`/post/${post.id}`}
                    data-testid={`read-more-${post.id}`}
                  >
                    <Button 
                      variant="ghost" 
                      className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 p-0 h-auto font-semibold group"
                    >
                      Read More 
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;